<script setup lang="ts">
import { ref, type PropType, computed } from 'vue'
import type { BlogThumbnailData } from '@/demo/DemoLatestNews.vue'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import { getCurrentGaPageName } from '@/lib/utils'

const isImageLoaded = ref(false)

const props = defineProps({
	blog: {
		type: Object as PropType<BlogThumbnailData>,
		default: () => ({}),
	},
	isDataLoaded: {
		type: Boolean,
		default: false,
	},
	isMercariBrand: {
		type: Boolean,
		default: false,
	},
})

const hoverClasses = computed(() => {
	if (props.isDataLoaded) {
		return 'hover:border-n-950 hover:shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10)]'
	}
	return ''
})

const showBlog = computed(() => {
	// Show the blog if the data is not yet loaded or if the blog object is not empty
	return (
		!props.isDataLoaded || (props.blog && Object.keys(props.blog).length > 0)
	)
})

function onClickItem(event: MouseEvent) {
	const pageName = getCurrentGaPageName()
	createGAEvent(GaGeneralEvent.ButtonClick, {
		...(pageName && { page_name: pageName }),
		button_name: 'Select Blog Post',
		link_url: props.blog.link,
		...(props.isMercariBrand && { event_extra: props.blog.title }),
	})
	event.preventDefault()
	window.open(props.blog.link, '_self')
}

function onImageLoadFinish() {
	isImageLoaded.value = true
}
</script>

<template>
	<div
		v-if="showBlog"
		class="flex w-[261px] flex-shrink-0 flex-col overflow-hidden rounded-rwd-lg border border-n-100 bg-white mdp:w-full"
		:class="hoverClasses"
	>
		<!-- Loading Skeleton -->
		<div v-if="!isDataLoaded" class="animate-pulse">
			<div class="aspect-video bg-n-100"></div>
			<div class="px-[16px] py-good-friends">
				<div class="mb-[20px] flex justify-between" v-if="!isMercariBrand">
					<div class="h-[10px] w-1/5 rounded-sm bg-n-100 mdp:h-[12px]"></div>
					<div class="h-[10px] w-1/3 rounded-sm bg-n-100 mdp:h-[12px]"></div>
				</div>

				<div class="mb-best-friends h-[16px] w-full rounded-sm bg-n-100"></div>
				<div class="h-[16px] w-3/4 rounded-sm bg-n-100"></div>
			</div>
		</div>

		<a v-else class="cursor-pointer" :href="blog.link" @click="onClickItem">
			<div
				v-if="!isImageLoaded"
				class="aspect-video animate-pulse bg-n-100"
			></div>
			<img
				:src="blog.img_medium"
				:alt="`image of '${blog.title}'`"
				loading="lazy"
				class="aspect-video object-cover"
				@load="onImageLoadFinish"
				@error="onImageLoadFinish"
			/>

			<div class="px-[16px] py-good-friends">
				<div
					class="typography-c2-reg mb-best-friends flex justify-between"
					v-if="!isMercariBrand"
				>
					<div class="text-o-500">
						<a :href="blog.categories[0].url">
							{{ blog.categories[0].name }}
						</a>
					</div>
					<div class="text-n-500">{{ blog.date }}</div>
				</div>

				<div class="typography-s1-emp line-clamp-2 text-ellipsis text-n-950">
					{{ blog.title }}
				</div>
			</div>
		</a>
	</div>
</template>
